import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as CONVERSATION_DETAILS_QUERY_KEY } from './useGetConversationDetails';

export type ApiRequest_MarkMessagesAsRead_Conversation = {
  conversation_id: string;
};

export type ApiResponse_MarkMessagesAsRead_Conversation = {
  success: boolean;
};

const METHOD = 'POST';
const ENDPOINT = 'conversation/:conversationSid/mark-read';

export const useMarkMessagesAsRead = (
  stateFns?: MutationStateFns<ApiResponse_MarkMessagesAsRead_Conversation>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_MarkMessagesAsRead_Conversation) =>
      handleApiCall<ApiResponse_MarkMessagesAsRead_Conversation>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, { conversationSid: data.conversation_id })
      ),
    onSuccess: (data, variables) => {
      // Invalidate conversation details to refresh read status
      queryClient.invalidateQueries({
        queryKey: CONVERSATION_DETAILS_QUERY_KEY({ conversation_id: variables.conversation_id }),
        refetchType: 'all',
      });

      // Invalidate inbox conversations to update read status in the list
      queryClient.invalidateQueries({
        predicate: query => {
          const { queryKey } = query;
          return Array.isArray(queryKey) && queryKey[1] === 'conversation/inbox';
        },
        refetchType: 'all',
      });

      // Invalidate unread inbox count to update menu badge
      queryClient.invalidateQueries({
        predicate: query => {
          const { queryKey } = query;
          return Array.isArray(queryKey) && queryKey[1] === 'conversation/inbox/unread-count';
        },
        refetchType: 'all',
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      stateFns?.onError?.(err);
    },
  });
};
